import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../contexts/UserContext';
import { useAppContext } from '../contexts/AppContext';
import { UserHeader } from '../components/app-shell/UserHeader';
import { Stack } from '../components/ui';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CompanyHomepage } from '../components/CompanyHomepage';
import { EquipmentContent } from '../components/EquipmentContent';
import { ReportsLandingPage } from '../components/reports/ReportsLandingPage';
import { StandardReportView } from '../components/reports/StandardReportView';
import { CustomReportBuilder } from '../components/reports/CustomReportBuilder';
import { InspectionReport } from '../components/InspectionReport';
import { Issues } from '../components/Issues';
import { IssueDetail } from '../components/IssueDetail';
import { iDashboardInterface } from '../types';
import { SiteAdminContent } from '../components/SiteAdminContent';
import { SiteAdminResourceLibrary } from '../components/ResourceLibraryContent';
import { CompanyResourcesPage } from '../components/company-resources/CompanyResourcesPage';
import { ScheduleSafety } from '../components/SafetyContent';
import { DailyBriefingPage } from '../components/pages-daily-briefing';
import { SafetyReport } from '../components/SafetyReport';
import { FormsContent } from '../components/FormsContent';
import { getUrl } from 'aws-amplify/storage';
import { SingleUnitContent } from '../components/SingleUnitContent';
import { SingleFormContent } from '../components/pages-forms/SingleFormContent';
import { TireModuleContent } from '../components/TireModuleContent';
import { PMDashboardContent } from '../components/pm/PMDashboardContent';
import { CostTrackingDashboardContent } from '../components/cost-tracking/CostTrackingDashboardContent';
import { SafetyTrainingDashboard } from '../components/safety-training/SafetyTrainingDashboard';
import { CustomerProfilePage } from '../components/CustomerProfilePage';
import { BusinessAdminHomepage } from '../components/BusinessAdminHomepage';
import { InboxContent } from '../components/inbox/InboxContent';
import { GetHelpContent } from '../components/get-help/GetHelpContent';
import { SettingsBillingPage } from '../components/subscription/SettingsBillingPage';
import { ManageSubscriptionPage } from '../components/subscription/ManageSubscriptionPage';
import { RegionsPage } from '../components/regions/RegionsPage';
import { UsersListPage } from '../components/users/UsersListPage';
import { SingleUserProfilePage } from '../components/users/SingleUserProfilePage';
import { MyProfilePage } from '../components/users/MyProfilePage';
import { SkillsetSetupLibraryPage } from '../components/users/SkillsetSetupLibraryPage';
import { GradeUserSkillsetsPage } from '../components/users/GradeUserSkillsetsPage';
import { LIBRARY_TEMPLATE_COMPANY_ID } from '../utils/constants';
import { InspectionOptions } from './InspectionOptions';
import { QRCodes } from './QRCodes';
import { NotFound } from './NotFound';

export const Dashboard = ({ page = '', noNavBar = false }: iDashboardInterface) => {
    const { companyUser, isLoading, setNoNavBar, companyOwner, businessAdmin } = useUserContext();
    const { inboxCount } = useAppContext();
    const [userImage, setUserImage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        setNoNavBar(noNavBar);
    }, [noNavBar, setNoNavBar]);

    useEffect(() => {
        const photoPath = companyUser?.userPhoto || null;
        if (!photoPath) {
            setUserImage('');
            return;
        }

        let isCancelled = false;
        (async () => {
            try {
                // Signed URLs expire; request a longer-lived URL and fall back if load fails.
                const photoUrl = await getUrl({
                    path: photoPath,
                    options: { expiresIn: 3600 },
                });
                if (!isCancelled) {
                    setUserImage(photoUrl.url.href);
                }
            } catch (error) {
                console.error('Error fetching image:', error);
                Sentry.captureException(error);
                if (!isCancelled) {
                    setUserImage('');
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [companyUser?.userPhoto]);

    const currentUser = companyOwner || companyUser || businessAdmin;
    if (!currentUser) {
        return isLoading ? (
            <LoadingSpinner isLoading content={<></>} />
        ) : null;
    }

    return (
        <LoadingSpinner
            isLoading={isLoading}
            keepMounted
            content={
                <Stack size="l">
                    <UserHeader
                        name={currentUser.name ?? ''}
                        imageSrc={userImage || undefined}
                        inboxCount={inboxCount}
                        onImageError={() => setUserImage('')}
                        onProfileClick={() => {
                            if (businessAdmin) {
                                navigate('/settings');
                            } else {
                                const userId = companyUser?.id || companyOwner?.id;
                                if (userId) navigate(`/user/${userId}`);
                            }
                        }}
                        onInboxClick={() => navigate('/inbox')}
                    />
                    <Sentry.ErrorBoundary
                        key={page}
                        fallback={
                            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                                <h3 style={{ color: '#1e293b', marginBottom: 8 }}>Something went wrong</h3>
                                <p style={{ fontSize: 14 }}>
                                    This page encountered an error. Try refreshing or navigating to a different page.
                                </p>
                                <button
                                    onClick={() => window.location.reload()}
                                    style={{
                                        marginTop: 12,
                                        padding: '8px 20px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: 8,
                                        background: '#fff',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                    }}
                                >
                                    Refresh Page
                                </button>
                            </div>
                        }
                    >
                        {!page ||
                            (page === 'home' && (businessAdmin ? <BusinessAdminHomepage /> : <CompanyHomepage />))}
                        {page === 'site admin' && <SiteAdminContent />}
                        {page === 'personnel' && <BusinessAdminHomepage />}
                        {page === 'forms' && <FormsContent />}
                        {page === 'new form' && <SingleFormContent />}
                        {page === 'edit form' && <SingleFormContent />}
                        {page === 'edit template' && <SingleFormContent />}
                        {page === 'equipment' && <EquipmentContent />}
                        {page === 'inspect' && <InspectionOptions />}
                        {page === 'qr-codes' && <QRCodes />}
                        {page === 'single unit' && <SingleUnitContent />}
                        {page === 'issues' && <Issues />}
                        {page === 'issue' && <IssueDetail />}
                        {page === 'issue-edit' && <IssueDetail editMode />}
                        {page === 'regions' && <RegionsPage />}
                        {page === 'users' && <UsersListPage />}
                        {page === 'reports' && <ReportsLandingPage />}
                        {page === 'standard-report' && (
                            <StandardReportView
                                companyId={'companyId' in currentUser ? (currentUser as any).companyId || '' : ''}
                                userId={currentUser?.id || ''}
                            />
                        )}
                        {(page === 'custom-report-new' || page === 'custom-report-edit') && (
                            <CustomReportBuilder
                                companyId={'companyId' in currentUser ? (currentUser as any).companyId || '' : ''}
                                userId={currentUser?.id || ''}
                                backTab="custom"
                            />
                        )}
                        {page === 'report' && <InspectionReport />}
                        {page === 'site admin resources' && <SiteAdminResourceLibrary />}
                        {page === 'resources' && <CompanyResourcesPage />}
                        {page === 'schedule' && <ScheduleSafety />}
                        {page === 'daily briefing' && <DailyBriefingPage />}
                        {page === 'safety training' && <SafetyTrainingDashboard />}
                        {page === 'admin-training' && (
                            <SafetyTrainingDashboard companyId={LIBRARY_TEMPLATE_COMPANY_ID} templateMode />
                        )}
                        {page === 'safety report' && <SafetyReport />}
                        {page === 'tire maintenance' && <TireModuleContent />}
                        {page === 'pm management' && <PMDashboardContent />}
                        {page === 'cost tracking' && <CostTrackingDashboardContent />}
                        {page === 'inbox' && <InboxContent />}
                        {page === 'help' && <GetHelpContent />}
                        {page === 'settings' && <SettingsBillingPage />}
                        {page === 'manage-subscription' && <ManageSubscriptionPage />}
                        {page === 'user-profile' && <SingleUserProfilePage />}
                        {page === 'my-profile' && <MyProfilePage />}
                        {page === 'user-skillsets' && <SkillsetSetupLibraryPage />}
                        {page === 'grade-user-skillsets' && <GradeUserSkillsetsPage />}
                        {page === 'customer-profile' && <CustomerProfilePage />}
                        {page === 'company-profile' && <CustomerProfilePage readOnly />}
                        {page === 'not-found' && (
                            <div className="page-container">
                                <NotFound />
                            </div>
                        )}
                    </Sentry.ErrorBoundary>
                </Stack>
            }
        />
    );
};
